import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CheckCircle2 } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'

export function SMSOptInExamplePage() {
  const [phoneNumber, setPhoneNumber] = useState('')
  const [consentGiven, setConsentGiven] = useState(false)

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Simple Pelgo Header */}
      <header className="border-b bg-white">
        <div className="container max-w-4xl py-4 px-4">
          <a href="/" className="text-xl font-bold text-slate-900 hover:text-slate-700">
            Pelgo
          </a>
        </div>
      </header>

      <div className="container max-w-4xl py-12 px-4">
        {/* Legal Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">SMS Consent Collection Example</h1>
          <p className="text-sm text-slate-600">
            Demonstration of compliant consent collection procedures pursuant to TCPA regulations
          </p>
        </div>

        {/* Disclaimer Alert */}
        <Alert className="mb-8 border-amber-200 bg-amber-50">
          <AlertDescription className="text-sm text-slate-700">
            <strong>DEMONSTRATION ONLY:</strong> This page serves as documentary evidence of Pelgo
            Inc.'s SMS consent collection procedures for telecommunications compliance verification.
            The submit button is intentionally disabled as this is a reference implementation only.
          </AlertDescription>
        </Alert>

        {/* Main Consent Form */}
        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-2xl">SMS Notification Enrollment</CardTitle>
            <CardDescription className="text-base">
              Pelgo Career Transition Support Program
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Phone Number Field */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-base font-medium">
                Mobile Phone Number (Optional)
              </Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="text-base"
              />
              <p className="text-xs text-slate-500">
                Optionally enter your mobile number to receive SMS notifications. You can use all
                Pelgo services without providing a phone number.
              </p>
            </div>

            {/* Consent Checkbox and Text */}
            <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-6">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="consent"
                  checked={consentGiven}
                  onCheckedChange={(checked) => setConsentGiven(checked === true)}
                  className="mt-1"
                />
                <div className="space-y-2 flex-1">
                  <Label
                    htmlFor="consent"
                    className="text-sm font-medium leading-relaxed cursor-pointer"
                  >
                    SMS Communication Consent (Optional)
                  </Label>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-3 pl-0">
                    <p>
                      By checking this box, I hereby provide my express written consent to receive
                      SMS text messages from Pelgo Inc. at the mobile phone number provided above.
                    </p>

                    <div className="space-y-2">
                      <p className="font-medium">I understand and agree that:</p>
                      <ul className="list-disc list-inside space-y-1 pl-4">
                        <li>
                          I am eligible to participate in Pelgo's career transition support program
                        </li>
                        <li>
                          SMS messages will include onboarding instructions, verification codes,
                          career transition reminders, and support notifications
                        </li>
                        <li>Message frequency may vary based on program activity</li>
                        <li>Standard message and data rates may apply</li>
                        <li>
                          <strong>Consent is not required to use Pelgo services</strong> - I can
                          access all features via web without SMS
                        </li>
                        <li>I may revoke this consent at any time by replying STOP</li>
                      </ul>
                    </div>

                    <p className="text-xs text-slate-600 italic">
                      This consent is provided as part of my enrollment in an employer-sponsored
                      career transition program administered by Pelgo Inc. My consent, including
                      timestamp and the mobile number provided, will be securely stored in
                      accordance with applicable data protection regulations.
                    </p>

                    <p className="text-xs text-slate-600 mt-3">
                      By submitting this form, you also acknowledge that you have read and agree to
                      our{' '}
                      <Link
                        to="/privacy"
                        className="text-blue-600 hover:text-blue-700 underline"
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      >
                        Privacy Policy
                      </Link>{' '}
                      and{' '}
                      <Link
                        to="/terms-of-service"
                        className="text-blue-600 hover:text-blue-700 underline"
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      >
                        Terms of Service
                      </Link>
                      .
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button (Disabled for Demo) */}
            <div className="space-y-2">
              <Button disabled className="w-full text-base py-6">
                <CheckCircle2 className="mr-2 h-5 w-5" />
                Submit Enrollment (Demo Only - Disabled)
              </Button>
              <p className="text-xs text-center text-slate-500">
                This is a demonstration page. The submit function is intentionally disabled.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Legal Documentation Section */}
        <div className="mt-8 space-y-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Documented Consent Collection Process</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-slate-700">
              <div>
                <h3 className="font-semibold mb-2">Proof of Consent:</h3>
                <p className="leading-relaxed">
                  Eligible individuals are identified by their employer as part of a career
                  transition program. Pelgo contacts each individual through a secure onboarding
                  email or portal. The individual may <strong>optionally</strong> enter their mobile
                  number and check a consent box to receive SMS notifications from Pelgo.{' '}
                  <strong>SMS opt-in is not required to use Pelgo services</strong> - users can
                  access all features via our web platform without providing a phone number. When
                  consent is given, it is stored with timestamp.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Use Case Description:</h3>
                <p className="leading-relaxed">
                  Pelgo sends onboarding instructions, verification codes, career transition
                  reminders, and support messages for individuals participating in Pelgo's
                  employer-sponsored program. Messages are sent only after the individual has opted
                  in directly.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Compliance Standards:</h3>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>TCPA (Telephone Consumer Protection Act) compliant</li>
                  <li>CTIA Messaging Principles and Best Practices</li>
                  <li>A2P 10DLC registration requirements</li>
                  <li>Express written consent with clear opt-out mechanism</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Footer Information */}
          <div className="text-center space-y-2 text-xs text-slate-500 pb-8">
            <p>Pelgo Inc.</p>
            <p>838 Walker Road, Suite 21-2, Dover, Delaware 19904</p>
            <p className="pt-2">
              This page serves as documentary evidence of SMS consent collection procedures for
              regulatory compliance verification purposes.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
